import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, map, Observable, startWith } from 'rxjs';
import { SHARED_COMPONENTS } from '../../../shared/shared-imports';
import { SharedService } from '../../../shared/services/shared.service';
import { RESOURCES, ResourceItem } from './resources.data';
import { OptionTranslateService } from '../../../shared/services/option-translate.service';

@Component({
  selector: 'app-service-manuals',
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS],
  templateUrl: './service-manuals.component.html',
  styleUrl: './service-manuals.component.scss',
})
export class ServiceManualsComponent {
  form: FormGroup;
  lang = 'en';
  resources = RESOURCES;
  resourceCategoriesList: { label: string; value: string }[] = [];
  filtered$: Observable<ResourceItem[]>;

  constructor(
    private fb: FormBuilder,
    private sharedService: SharedService,
    private optionTranslateService:OptionTranslateService
  ) {
    this.lang = (this.sharedService.getCurrentLang() || 'en').toLowerCase();
     this.resourceCategoriesList = this.optionTranslateService.getOptions('ResourceCategories',
      this.lang
    );
    //this.resourceCategoriesList = this.buildCategoryOptions();
    this.form = this.fb.group({
      category: [null],
      search: [''],
    });

    this.filtered$ = combineLatest([
      this.form.get('category')!.valueChanges.pipe(startWith(null)),
      this.form.get('search')!.valueChanges.pipe(
        startWith(''),
        map((v: string) => (v || '').toLowerCase().trim()),
      ),
    ]).pipe(
      map(([cat, term]) =>
        this.resources.filter(resource => {
          const matchesCategory = this.matchesCategory(cat, resource.categoryName);
          const matchesSearch = !term || this.matches(resource, term);
          return matchesCategory && matchesSearch;
        }),
      ),
    );
  }

  private matchesCategory(selected: string | null, categoryName: string): boolean {
    if (selected === null) {
      return true;
    }
    const normalize = (value: string) => (value || '').toLowerCase().replace(/\s+/g, '');
    const normalizedSelected = normalize(selected);
    if (normalizedSelected === 'other') {
      const excluded = ['laws', 'reports', 'service manual', 'instruction videos'];
      const cat = (categoryName || '').toLowerCase();
      return !excluded.some(term => cat.includes(term));
    }
    return normalize(categoryName) === normalizedSelected;
  }

  private buildCategoryOptions(): { label: string; value: string }[] {
    const uniqueCategories = Array.from(
      new Map(this.resources.map(item => [item.categoryName, item])).values(),
    );
    const options = uniqueCategories.map(item => ({
      label: this.lang === 'ar' ? item.categoryAr : item.categoryEn,
      value: item.categoryName,
    }));
    const allLabel = this.lang === 'ar' ? '\u0627\u0644\u0643\u0644' : 'All';
    return [{ label: allLabel, value: 'all' }, ...options];
  }

  private matches(resource: ResourceItem, term: string): boolean {
    const title = this.lang === 'ar' ? resource.titleAr : resource.titleEn;
    const desc = this.lang === 'ar' ? resource.descriptionAr : resource.descriptionEn;
    return [title, desc, resource.categoryName].some(text =>
      (text || '').toLowerCase().includes(term),
    );
  }

  open(resource: ResourceItem) {
    const url = this.lang === 'ar' ? resource.URL_AR : resource.URL_EN;
    if (url) {
      window.open(url, '_blank');
    }
  }
}
